/**
 * Phase 2A — Unified Assessment Editor (read-only shell).
 *
 * Implements the three-pane editor frame:
 *   ┌──────────────── TopBar (56px) ────────────────┐
 *   │ Sidebar │      Canvas       │   Inspector     │
 *   └──────────────── StatusBar (32px) ──────────────┘
 *
 * Phase 2A scope (read-only):
 *   - Blueprint view (default landing at /admin/studio/edit/:id)
 *   - Canvas Compose mode (read-only render of sections + content)
 *   - Sidebar outline (sections + content rows, read-only)
 *   - Inspector with 3 tabs: Overview · Recommendations (rule-based) · Inspector (focus-aware)
 *   - Live Assessment Summary strip + StatusBar
 *   - Preview button → stub drawer reserving 4 personas
 *
 * Editing, drag-and-drop, autosave, and publish workflows land in Phase 2B/2C.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft, ChevronDown, ChevronRight, Eye, FileText, Layers,
  Loader2, Sparkles, Target, Award, Clock, Users, Compass,
  ShieldCheck, AlertTriangle, CheckCircle2, ListChecks, History, Plus, Trash2,
  Table as TableIcon, LayoutList, ChevronUp, Circle, Pencil,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { adminApi } from '@/lib/adminApi';
import { api, formatApiError } from '@/lib/api';
import { useApp } from '@/context/AppContext';
import { LifecyclePill } from '@/components/studio/LifecyclePill';
import LanguageSwitcher from '@/components/layout/LanguageSwitcher';
import { DifficultyPicker, difficultyLabel } from '@/components/studio/DifficultyPicker';
import {
  AutosaveIndicator, SaveHistorySheet, RecoveryDialog, UnsavedChangesDialog,
  SessionTimer,
} from '@/components/studio/AutosaveComponents';
import {
  EditorBreadcrumb, UndoRedoButtons, useEditorHistory, useEditorHotkeys,
} from '@/components/studio/EditorSupport';
import { QuestionNavigator } from '@/components/studio/QuestionNavigator';
import { CanvasBulkMode } from '@/components/studio/CanvasBulkMode';
import {
  ValidationDrawer, PublishReadinessChip, useValidation,
  deriveNavigatorStatuses, LastBulkActionToast,
} from '@/components/studio/ValidationDrawer';
import { VersionSidebar } from '@/components/studio/VersionSidebar';
import { PreviewDrawer } from '@/components/studio/PreviewDrawer';
import {
  PublishSplitButton, PublishDialog, PublishSuccessDialog,
  PublishedBadge, FindReplaceDialog,
} from '@/components/studio/PublishWorkflow';
import { ASSET_TYPE_ICONS, LIFECYCLE_COLOR_MAP } from '@/lib/assetTypes';
import { useAutosave, readRecovery, clearRecovery } from '@/hooks/useAutosave';
import { toast } from 'sonner';

/**
 * Route helpers.
 *   /admin/studio/edit/:id            → Blueprint
 *   /admin/studio/edit/:id?view=canvas → Editor canvas
 */
export default function AdminStudioEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const { user } = useApp();
  const view = searchParams.get('view') || 'blueprint';

  const [data, setData] = useState(null);     // { questionnaire, versions[] }
  const [meta, setMeta] = useState(null);     // studio meta registry
  const [loading, setLoading] = useState(true);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [validationOpen, setValidationOpen] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [publishSuccess, setPublishSuccess] = useState(null);  // { summary }
  const [findReplaceOpen, setFindReplaceOpen] = useState(false);
  const [recovery, setRecovery] = useState(null);
  const [unsavedDialog, setUnsavedDialog] = useState(null);  // { onConfirm }
  // Phase 2C.1 — track the last save_id + its actions to drive the Undo toast.
  const [lastSave, setLastSave] = useState(null);  // { saveId, actions, snapshotBeforeSave }
  const lastSnapshotBeforeSaveRef = useRef(null);
  // Phase 2C.1 — bulk-intent toast bridge. CanvasBulkMode notifies the parent
  // immediately on bulk apply; the toast pops without waiting for the autosave
  // round-trip and the actual backend `actions` payload.
  const [pendingBulkIntent, setPendingBulkIntent] = useState(null);
  // Phase 2C.1 — when an issue is clicked in the ValidationDrawer we need to
  // force the inspector tab to 'focus' even if the user is not on Overview.
  const [inspectorTabRequest, setInspectorTabRequest] = useState(null);

  // ---------- Load asset + meta ----------
  useEffect(() => {
    setLoading(true);
    Promise.all([adminApi.getQuestionnaire(id), adminApi.studioMeta()])
      .then(([q, m]) => { setData(q); setMeta(m); })
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, [id]);

  // ---------- Autosave hook ----------
  const questionnaire = data?.questionnaire || null;
  const latestVersion = useMemo(() => {
    if (!data?.versions?.length) return null;
    return data.versions[data.versions.length - 1];
  }, [data]);
  const editable = questionnaire && ['draft', 'needs_revision'].includes((questionnaire.status || 'draft').toLowerCase());
  // Founder correction batch §3-7 — HM Admin may quick-edit governable
  // metadata from the review page at ANY lifecycle status (audited backend
  // path); structural/content editing remains draft-only in the Editor.
  const isHmAdmin = user?.role === 'hm_admin';
  const metaEditable = !!(editable || (questionnaire && isHmAdmin));

  const autosave = useAutosave({
    assetId: id,
    initialMetaVersion: questionnaire?.meta_version || 0,
    onSaved: (res, extra) => {
      // UAT Finding #1 — when edits queued during the in-flight save exist,
      // local state is NEWER than this response; skip the merge so the UI
      // doesn't momentarily revert (the follow-up save persists them).
      if (!extra?.hasPending) {
        setData((prev) => prev ? {
          ...prev,
          questionnaire: { ...prev.questionnaire, ...(res.questionnaire || {}) },
          versions: res.latest_version
            ? mergeLatest(prev.versions, res.latest_version)
            : prev.versions,
        } : prev);
      }
      // Track the last save_id + its semantic actions so the Bulk Undo toast
      // can decide whether to render. Also store the snapshot that existed
      // BEFORE the save so a single Undo can restore it.
      const actions = res.editor_audit_actions || res.actions || [];
      setLastSave({
        saveId: res.save_id,
        actions,
        snapshotBeforeSave: lastSnapshotBeforeSaveRef.current,
      });
    },
    onConflict: () => {
      toast.error(t('studio.autosave.conflictToast', {
        defaultValue: 'Conflict — refresh to see the latest changes.',
      }));
    },
  });

  // ---------- Recovery dialog wiring (R2) ----------
  useEffect(() => {
    const onRecovery = (e) => {
      if (e.detail?.assetId === id) setRecovery(e.detail);
    };
    window.addEventListener('studio:recovery-available', onRecovery);
    return () => window.removeEventListener('studio:recovery-available', onRecovery);
  }, [id]);

  // ---------- In-app navigation guard (R3) ----------
  // BrowserRouter doesn't expose useBlocker. We guard our own nav actions
  // (Back to Studio, Blueprint link, etc.) by calling `guardedNavigate`.
  const guardedNavigate = useCallback((target) => {
    if (autosave.hasPendingChanges()) {
      setUnsavedDialog({
        onCancel: () => setUnsavedDialog(null),
        onConfirmLeave: () => { setUnsavedDialog(null); navigate(target); },
        onSaveAndLeave: async () => {
          // UAT Finding #1 — only leave once the backend acknowledged ALL
          // pending edits; otherwise keep the author in the editor.
          const ok = await autosave.flush();
          setUnsavedDialog(null);
          if (ok) {
            navigate(target);
          } else {
            toast.error(t('studio.unsaved.exitBlocked', {
              defaultValue: 'Some changes could not be saved — please retry before leaving.',
            }));
          }
        },
      });
    } else {
      navigate(target);
    }
  }, [autosave, navigate]);

  // ---------- Cmd+S → force save ----------
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        autosave.forceSave();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [autosave]);

  // ---------- Derived state ----------
  const sections = latestVersion?.sections || [];
  const questions = latestVersion?.questions || [];
  const totalCompetencies = useMemo(() => {
    const s = new Set();
    questions.forEach((q) => Object.keys(q.competency_map || {}).forEach((k) => s.add(k)));
    return s.size;
  }, [questions]);
  const estimatedMinutes = questionnaire?.estimated_minutes ?? estimateMinutes(questions);
  const assetTypeMeta = meta?.asset_types?.find((a) => a.code === questionnaire?.type) || null;

  // ---------- Mutators (call into autosave) ----------
  const patchMeta = useCallback((partial) => {
    if (!editable) {
      toast.error(t('studio.autosave.notEditable', { defaultValue: 'Read-only: revert to draft to edit.' }));
      return;
    }
    setData((prev) => prev ? { ...prev, questionnaire: { ...prev.questionnaire, ...partial } } : prev);
    autosave.queuePatch({ meta: partial });
  }, [editable, autosave, t]);

  // Governance quick-edit path (debounced) — used when the asset is not in an
  // editable draft state but the actor is HM Admin.
  const govTimerRef = useRef(null);
  const govBufferRef = useRef({});
  const governancePatch = useCallback((partial) => {
    if (editable) { patchMeta(partial); return; }
    if (!isHmAdmin) {
      toast.error(t('studio.autosave.notEditable', { defaultValue: 'Read-only: revert to draft to edit.' }));
      return;
    }
    setData((prev) => prev ? { ...prev, questionnaire: { ...prev.questionnaire, ...partial } } : prev);
    govBufferRef.current = { ...govBufferRef.current, ...partial };
    clearTimeout(govTimerRef.current);
    govTimerRef.current = setTimeout(async () => {
      const body = govBufferRef.current;
      govBufferRef.current = {};
      try {
        await api.patch(`/admin/studio/assessments/${id}/governance-meta`, body);
        toast.success(t('studio.governance.saved', { defaultValue: 'Metadatos de gobernanza guardados' }));
      } catch (err) {
        toast.error(formatApiError(err));
      }
    }, 700);
  }, [editable, isHmAdmin, patchMeta, id, t]);

  const patchSections = useCallback((nextSections) => {
    if (!editable) return;
    setData((prev) => {
      if (!prev?.versions?.length) return prev;
      const versions = [...prev.versions];
      const last = { ...versions[versions.length - 1], sections: nextSections };
      versions[versions.length - 1] = last;
      return { ...prev, versions };
    });
    autosave.queuePatch({ version: { sections: nextSections, questions } });
  }, [editable, autosave, questions]);

  const patchQuestions = useCallback((nextQuestions) => {
    if (!editable) return;
    // Snapshot the pre-patch state so the Bulk Undo toast can restore it.
    lastSnapshotBeforeSaveRef.current = { sections, questions };
    setData((prev) => {
      if (!prev?.versions?.length) return prev;
      const versions = [...prev.versions];
      const last = { ...versions[versions.length - 1], questions: nextQuestions };
      versions[versions.length - 1] = last;
      return { ...prev, versions };
    });
    autosave.queuePatch({ version: { sections, questions: nextQuestions } });
  }, [editable, autosave, sections, questions]);

  // ---------- Editor focus state (lifted from EditorThreePane for breadcrumb) ----------
  const [focus, setFocus] = useState(null); // {kind: 'section'|'content', id}

  // ---------- Reorder handlers (Phase 2B.2 DnD) ----------
  const handleReorderSections = useCallback((reorderedSections) => {
    if (!editable) return;
    patchSections(reorderedSections);
  }, [editable, patchSections]);

  const handleReorderQuestion = useCallback((questionId, targetSectionId, targetIndex) => {
    if (!editable) return;
    const moving = questions.find((q) => q.id === questionId);
    if (!moving) return;
    // Remove from current position
    let remaining = questions.filter((q) => q.id !== questionId);
    // Build the target section's question list
    const inTarget = remaining.filter((q) => q.section_id === targetSectionId);
    const outsideTarget = remaining.filter((q) => q.section_id !== targetSectionId);
    const movedQ = { ...moving, section_id: targetSectionId };
    if (targetIndex == null || targetIndex < 0 || targetIndex > inTarget.length) {
      inTarget.push(movedQ);
    } else {
      inTarget.splice(targetIndex, 0, movedQ);
    }
    // Renumber order within the target section
    inTarget.forEach((q, i) => { q.order = i; });
    const next = [...outsideTarget, ...inTarget];
    patchQuestions(next);
  }, [editable, questions, patchQuestions]);

  // ---------- Undo/Redo (A2 architecture) ----------
  const history = useEditorHistory({ sections, questions });
  // Push a snapshot only on saving→saved transitions to avoid render loops.
  const prevAutosaveStateRef = useRef(autosave.state);
  useEffect(() => {
    if (prevAutosaveStateRef.current === 'saving' && autosave.state === 'saved') {
      history.push({ sections, questions });
    }
    prevAutosaveStateRef.current = autosave.state;
  }, [autosave.state]);
  const handleUndo = useCallback(() => {
    const snap = history.undo();
    if (snap) {
      patchSections(snap.sections);
      patchQuestions(snap.questions);
    }
  }, [history, patchSections, patchQuestions]);
  const handleRedo = useCallback(() => {
    const snap = history.redo();
    if (snap) {
      patchSections(snap.sections);
      patchQuestions(snap.questions);
    }
  }, [history, patchSections, patchQuestions]);
  useEditorHotkeys({ onUndo: handleUndo, onRedo: handleRedo });

  // ---------- Phase 2C.1 — Validation hook ----------
  const validation = useValidation(id, adminApi);
  // Auto re-validate after every successful save (debounced via state machine).
  const prevAutosaveStateForValRef = useRef(autosave.state);
  useEffect(() => {
    if (prevAutosaveStateForValRef.current === 'saving' && autosave.state === 'saved') {
      validation.run();
    }
    prevAutosaveStateForValRef.current = autosave.state;
  }, [autosave.state]);
  // Initial validation when the editor loads with content.
  useEffect(() => {
    if (data?.versions?.length) validation.run();
  }, [data?.versions?.length]);

  // Derived status maps for the Navigator (lights up the reserved slots).
  const { contentStatus, sectionStatus } = useMemo(
    () => deriveNavigatorStatuses(validation.envelope),
    [validation.envelope],
  );

  // Click an issue in the drawer → focus that entity in the editor.
  const handleFocusEntity = useCallback((issue) => {
    if (!issue?.entity_id) return;
    setSearchParams({ view: 'canvas' });
    setFocus({ kind: issue.entity_kind === 'section' ? 'section' : 'content', id: issue.entity_id });
    setInspectorTabRequest('focus');
    // Reset the request a tick later so subsequent clicks still trigger the effect.
    setTimeout(() => setInspectorTabRequest(null), 0);
    setValidationOpen(false);
  }, [setSearchParams]);

  // CanvasBulkMode tells us a bulk op was applied. Snapshot + intent both stored.
  const handleBulkApplied = useCallback((descriptor) => {
    lastSnapshotBeforeSaveRef.current = descriptor.priorSnapshot;
    setPendingBulkIntent({
      ...descriptor,
      id: `intent_${Date.now().toString(36)}`,
    });
  }, []);

  // Undo last bulk action by restoring the snapshot taken just before the save.
  const handleBulkUndo = useCallback(() => {
    const snap = pendingBulkIntent?.priorSnapshot
                || lastSave?.snapshotBeforeSave;
    if (!snap) {
      toast.error(t('studio.bulk.undoUnavailable', { defaultValue: 'Nothing to undo for this save.' }));
      return;
    }
    if (snap.sections) patchSections(snap.sections);
    if (snap.questions) patchQuestions(snap.questions);
    setPendingBulkIntent(null);
    setLastSave((prev) => prev ? { ...prev, snapshotBeforeSave: null } : prev);
    toast.success(t('studio.bulk.undoApplied', { defaultValue: 'Bulk action reverted.' }));
  }, [pendingBulkIntent, lastSave, patchSections, patchQuestions, t]);

  // ---------- Loading / not-found gates ----------
  if (loading) {
    return (
      <FullScreen>
        <Loader2 className="h-7 w-7 animate-spin text-primary" />
      </FullScreen>
    );
  }
  if (!questionnaire) {
    return (
      <FullScreen>
        <p className="text-sm text-muted-foreground">
          {t('studio.editor.notFound', { defaultValue: 'Assessment not found.' })}
        </p>
        <Button onClick={() => navigate('/admin/studio')} variant="outline" size="sm" className="mt-4">
          {t('studio.editor.backToStudio', { defaultValue: 'Back to Studio' })}
        </Button>
      </FullScreen>
    );
  }

  // ---------- Live summary (F3) ----------
  const summary = {
    sections: sections.length,
    content: questions.length,
    competencies: totalCompetencies,
    minutes: estimatedMinutes,
    difficulty: questionnaire.difficulty ?? 3,
    // Profile-type assets are voluntary forms — no score/difficulty applies.
    scoringNa: questionnaire.type === 'profile',
    health: deriveHealth(questionnaire, sections, questions),
    // A9 — canonical publication readiness comes from the Validation Center.
    valErrors: validation?.envelope?.severity_counts?.error ?? null,
  };

  // ---------- Render ----------
  return (
    <div className="h-screen flex flex-col bg-background" data-testid="studio-editor-page">
      {/* ============ TOP BAR ============ */}
      <EditorTopBar
        questionnaire={questionnaire}
        latestVersion={latestVersion}
        userRole={user?.role}
        userId={user?.id}
        editable={editable}
        autosave={autosave}
        history={history}
        validation={validation}
        focusedSection={focus?.kind === 'section' ? sections.find((s) => s.id === focus.id) : (focus?.kind === 'content' ? sections.find((s) => s.id === questions.find((q) => q.id === focus.id)?.section_id) : null)}
        focusedContent={focus?.kind === 'content' ? questions.find((q) => q.id === focus.id) : null}
        onUndo={handleUndo}
        onRedo={handleRedo}
        onBack={() => (view === 'canvas'
          ? setSearchParams({ view: 'blueprint' })
          : guardedNavigate('/admin/studio'))}
        backLabel={view === 'canvas'
          ? t('studio.editor.backToPlan', { defaultValue: 'Back to Plan' })
          : t('studio.editor.backToStudio', { defaultValue: 'Back to Studio' })}
        onPreview={() => setPreviewOpen(true)}
        onOpenHistory={() => setHistoryOpen(true)}
        onOpenValidation={() => setValidationOpen(true)}
        onOpenVersions={() => setVersionsOpen(true)}
        onOpenPublish={() => setPublishDialogOpen(true)}
        onLifecycleUpdated={(updated) => setData((prev) => ({ ...prev, questionnaire: { ...prev.questionnaire, ...updated } }))}
      />

      {/* ============ MAIN CONTENT ============ */}
      <div className="flex-1 overflow-hidden flex">
        {view === 'blueprint' ? (
          <BlueprintView
            questionnaire={questionnaire}
            assetTypeMeta={assetTypeMeta}
            meta={meta}
            summary={summary}
            validation={validation}
            editable={editable}
            metaEditable={metaEditable}
            patchMeta={patchMeta}
            governancePatch={governancePatch}
            onOpenEditor={() => setSearchParams({ view: 'canvas' })}
            onOpenValidation={() => setValidationOpen(true)}
          />
        ) : (
          <EditorThreePane
            questionnaire={questionnaire}
            sections={sections}
            questions={questions}
            assetTypeMeta={assetTypeMeta}
            meta={meta}
            summary={summary}
            editable={editable}
            focus={focus}
            setFocus={setFocus}
            patchMeta={patchMeta}
            patchSections={patchSections}
            patchQuestions={patchQuestions}
            onReorderSections={handleReorderSections}
            onReorderQuestion={handleReorderQuestion}
            onBackToBlueprint={() => setSearchParams({ view: 'blueprint' })}
            contentStatus={contentStatus}
            sectionStatus={sectionStatus}
            onBulkApplied={handleBulkApplied}
            inspectorTabRequest={inspectorTabRequest}
            onOpenFindReplace={() => setFindReplaceOpen(true)}
            validationEnvelope={validation.envelope}
            onOpenValidation={() => setValidationOpen(true)}
          />
        )}
      </div>

      {/* ============ STATUS BAR ============ */}
      <EditorStatusBar summary={summary} sessionStartedAt={autosave.sessionStartedAt} />

      {/* ============ VALIDATION DRAWER (Phase 2C.1) ============ */}
      <ValidationDrawer
        open={validationOpen}
        onOpenChange={setValidationOpen}
        envelope={validation.envelope}
        loading={validation.loading}
        onRevalidate={validation.run}
        onFocusEntity={handleFocusEntity}
        locale={i18n.language}
        persistKey={id}
      />

      {/* ============ VERSION SIDEBAR (Phase 2C.2) ============ */}
      <VersionSidebar
        open={versionsOpen}
        onOpenChange={setVersionsOpen}
        assetId={id}
        adminApi={adminApi}
        onAfterRestore={async () => {
          // After a restore the latest draft is rewritten on the server.
          // Reload the editor data to reflect.
          try {
            const fresh = await adminApi.getQuestionnaire(id);
            setData(fresh);
            validation.run();
          } catch (err) {
            window.location.reload();
          }
        }}
      />

      {/* ============ PUBLISH WORKFLOW (Phase 2C.4) ============ */}
      <PublishDialog
        open={publishDialogOpen}
        onClose={() => setPublishDialogOpen(false)}
        assetId={id}
        questionnaire={{ ...questionnaire, _sectionsCount: sections.length, _itemsCount: questions.length }}
        adminApi={adminApi}
        onPublished={(out) => {
          setPublishDialogOpen(false);
          setData((prev) => ({ ...prev, questionnaire: { ...prev.questionnaire, ...(out.assessment || {}) } }));
          setPublishSuccess(out.summary);
          validation.run();
        }}
      />
      <PublishSuccessDialog
        open={!!publishSuccess}
        summary={publishSuccess}
        onClose={() => setPublishSuccess(null)}
        onViewCandidate={() => {
          setPublishSuccess(null);
          setPreviewOpen(true);
        }}
      />

      {/* ============ FIND & REPLACE (Phase 2C.4) ============
          We flush any in-flight autosave BEFORE applying the bulk patch so the
          F&R Replace-All doesn't race with the debounced autosave and produce
          a spurious 409 conflict toast on top of the success toast. */}
      <FindReplaceDialog
        open={findReplaceOpen}
        onClose={() => setFindReplaceOpen(false)}
        questions={questions}
        patchQuestions={async (next) => {
          try { await autosave.flush?.(); } catch (e) { /* best-effort */ }
          patchQuestions(next);
        }}
      />

      {/* ============ LAST BULK ACTION TOAST (Phase 2C.1) ============ */}
      <LastBulkActionToast
        saveId={pendingBulkIntent?.id || lastSave?.saveId}
        actions={
          pendingBulkIntent
            ? [{
                code: pendingBulkIntent.code,
                count: pendingBulkIntent.count,
                to: pendingBulkIntent.to,
                entity_ids: pendingBulkIntent.entity_ids,
              }]
            : lastSave?.actions
        }
        onUndo={handleBulkUndo}
      />

      {/* ============ PREVIEW DRAWER (Phase 2C.3) ============ */}
      <PreviewDrawer
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        questionnaire={questionnaire}
        sections={sections}
        questions={questions}
        apiBase={process.env.REACT_APP_BACKEND_URL}
        authToken={null}
      />

      {/* ============ SAVE HISTORY (R1) ============ */}
      <SaveHistorySheet
        assetId={id}
        open={historyOpen}
        onOpenChange={setHistoryOpen}
      />

      {/* ============ RECOVERY DIALOG (R2) ============ */}
      <RecoveryDialog
        open={!!recovery}
        snapshot={recovery}
        onRestore={() => {
          if (recovery?.pending?.meta_patch) {
            patchMeta(recovery.pending.meta_patch);
          }
          if (recovery?.pending?.version_patch) {
            const v = recovery.pending.version_patch;
            if (v.sections) patchSections(v.sections);
            if (v.questions) patchQuestions(v.questions);
          }
          clearRecovery(id);
          setRecovery(null);
          toast.success(t('studio.recovery.restoredToast', { defaultValue: 'Session restored.' }));
        }}
        onDiscard={() => { clearRecovery(id); setRecovery(null); }}
      />

      {/* ============ UNSAVED CHANGES (R3) ============ */}
      <UnsavedChangesDialog
        open={!!unsavedDialog}
        onCancel={() => unsavedDialog?.onCancel?.()}
        onConfirmLeave={() => unsavedDialog?.onConfirmLeave?.()}
        onSaveAndLeave={() => unsavedDialog?.onSaveAndLeave?.()}
      />
    </div>
  );
}

/* ============================================================================
 *                          TOP BAR
 * ========================================================================== */

function EditorTopBar({ questionnaire, latestVersion, userRole, userId, editable, autosave, history, validation, focusedSection, focusedContent, onUndo, onRedo, onBack, backLabel, onPreview, onOpenHistory, onOpenValidation, onOpenVersions, onOpenPublish, onLifecycleUpdated }) {
  const { t } = useTranslation();
  return (
    <header
      className="min-h-14 border-b border-border bg-card px-4 py-1.5 flex items-center flex-wrap gap-x-3 gap-y-1.5 shrink-0"
      data-testid="studio-editor-topbar"
    >
      <Button variant="ghost" size="sm" onClick={onBack} data-testid="studio-editor-back" className="whitespace-nowrap">
        <ArrowLeft className="h-4 w-4 mr-1.5" />
        {backLabel || t('studio.editor.backToStudio', { defaultValue: 'Back to Studio' })}
      </Button>
      <Separator orientation="vertical" className="h-6" />
      <div className="min-w-0 flex-1 flex flex-col">
        <EditorBreadcrumb
          assetName={questionnaire.name}
          section={focusedSection}
          content={focusedContent}
        />
        <p className="text-xs text-muted-foreground truncate">
          {questionnaire.code}
        </p>
      </div>
      {editable && history && (
        <UndoRedoButtons
          canUndo={history.canUndo}
          canRedo={history.canRedo}
          onUndo={onUndo}
          onRedo={onRedo}
        />
      )}
      <AutosaveIndicator
        state={autosave.state}
        lastSavedAt={autosave.lastSavedAt}
        lastError={autosave.lastError}
        onRetry={autosave.retry}
        onOpenHistory={onOpenHistory}
      />
      <Button
        variant="outline"
        size="sm"
        onClick={onOpenVersions}
        data-testid="studio-editor-versions-button"
        className="h-7 text-xs gap-1"
        title={t('studio.versions.title', { defaultValue: 'Versions' })}
      >
        <History className="h-3 w-3" />
        {t('studio.versions.title', { defaultValue: 'Versions' })}
      </Button>
      <PublishReadinessChip
        envelope={validation?.envelope}
        loading={validation?.loading}
        onClick={onOpenValidation}
      />
      <LifecyclePill
        assessment={questionnaire}
        userRole={userRole}
        userId={userId}
        onUpdated={onLifecycleUpdated}
      />
      <PublishedBadge questionnaire={questionnaire} />
      {userRole === 'hm_admin' && questionnaire?.status === 'approved' && (
        <PublishSplitButton
          questionnaire={questionnaire}
          onOpenPublish={onOpenPublish}
        />
      )}
      {latestVersion && (
        <Badge variant="outline" className="text-xs gap-1" data-testid="studio-editor-version-chip">
          <Sparkles className="h-3 w-3" /> v{latestVersion.version_number}
        </Badge>
      )}
      {!editable && (
        <Badge variant="outline" className="text-xs uppercase tracking-wide bg-amber-50 text-amber-900 border-amber-300 dark:bg-amber-500/15 dark:text-amber-200 dark:border-amber-500/40" data-testid="studio-editor-readonly-chip">
          {userRole === 'hm_admin'
            ? t('studio.editor.reviewMetaChip', { defaultValue: 'Revisión · metadatos editables · contenido en Editor' })
            : t('studio.editor.readOnlyChip', { defaultValue: 'Read-only' })}
        </Badge>
      )}
      <Button variant="outline" size="sm" onClick={onPreview} data-testid="studio-editor-preview">
        <Eye className="h-4 w-4 mr-1.5" />
        {t('studio.editor.preview', { defaultValue: 'Preview' })}
      </Button>
      <LanguageSwitcher compact />
    </header>
  );
}

/* ============================================================================
 *                          BLUEPRINT VIEW (F2)
 * ========================================================================== */

function BlueprintView({ questionnaire, assetTypeMeta, meta, summary, validation, editable, metaEditable, patchMeta, governancePatch, onOpenEditor, onOpenValidation }) {
  const { t, i18n } = useTranslation();
  const isEs = i18n.language?.startsWith('es');
  const Icon = ASSET_TYPE_ICONS[questionnaire.type] || FileText;
  const typeLabel = assetTypeMeta ? (isEs ? assetTypeMeta.label_es : assetTypeMeta.label_en) : (questionnaire.type || '—');
  const publishPersisted = questionnaire.publish_target || null;
  const publishSuggested = questionnaire.publish_target_suggestion || null;
  const publishMeta = meta?.publish_targets?.find((p) => p.value === (publishPersisted || publishSuggested));
  const publishBaseLabel = publishMeta ? (isEs ? publishMeta.label_es : publishMeta.label_en) : '—';
  const publishLabel = publishPersisted
    ? publishBaseLabel
    : (publishSuggested
        ? `${t('studio.blueprint.suggestedPrefix', { defaultValue: 'Suggested' })}: ${publishBaseLabel}`
        : '—');
  const stageMeta = meta?.lifecycle_stages?.find((s) => s.value === questionnaire.status);
  const stageLabel = stageMeta ? (isEs ? stageMeta.label_es : stageMeta.label_en) : questionnaire.status;

  const naLabel = t('studio.blueprint.notApplicable', { defaultValue: isEs ? 'No aplica' : 'Not applicable' });
  const scoringNa = questionnaire.type === 'profile';
  const cards = [
    { key: 'assetType', label: t('studio.blueprint.assetType', { defaultValue: 'Asset Type' }), value: typeLabel, icon: Icon },
    { key: 'purpose', label: t('studio.blueprint.purpose', { defaultValue: 'Purpose' }), value: questionnaire.objective || '—', icon: Compass, multiline: true },
    { key: 'audience', label: t('studio.blueprint.audience', { defaultValue: 'Audience' }), value: questionnaire.audience || questionnaire.professional_area || '—', icon: Users },
    { key: 'estimatedTime', label: t('studio.blueprint.estimatedTime', { defaultValue: 'Estimated Time' }), value: summary.minutes ? `${summary.minutes} min` : '—', icon: Clock },
    { key: 'difficulty', label: t('studio.blueprint.difficulty', { defaultValue: 'Difficulty' }), value: scoringNa ? naLabel : `D${summary.difficulty} · ${difficultyLabel(t, summary.difficulty)}`, icon: Layers },
    { key: 'passingScore', label: t('studio.blueprint.passingScore', { defaultValue: 'Passing Score' }), value: scoringNa ? naLabel : (questionnaire.passing_score != null ? `${questionnaire.passing_score}%` : '—'), icon: Award },
    { key: 'competencies', label: t('studio.blueprint.competencies', { defaultValue: 'Competencies' }), value: scoringNa ? naLabel : t('studio.blueprint.competenciesCount', { defaultValue: '{{n}} mapped', n: summary.competencies }), icon: Target },
    { key: 'publishTarget', label: t('studio.blueprint.publishTarget', { defaultValue: 'Publish Target' }), value: publishLabel, icon: Sparkles },
    { key: 'status', label: t('studio.blueprint.status', { defaultValue: 'Current Status' }), value: stageLabel, icon: ShieldCheck, tone: LIFECYCLE_COLOR_MAP[questionnaire.status]?.bg },
  ];

  return (
    <div className="flex-1 overflow-y-auto" data-testid="studio-blueprint-view">
      <div className="max-w-6xl mx-auto p-8 space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">
              {t('studio.blueprint.heading', { defaultValue: 'Assessment Blueprint' })}
            </p>
            <h1 className="font-display text-2xl lg:text-3xl font-bold tracking-tight">
              {questionnaire.name}
            </h1>
            {questionnaire.description && (
              <p className="text-sm text-muted-foreground mt-1.5 max-w-3xl">
                {questionnaire.description}
              </p>
            )}
          </div>
          <Button onClick={onOpenEditor} size="lg" data-testid="studio-blueprint-open-editor">
            <ListChecks className="h-4 w-4 mr-2" />
            {t('studio.blueprint.openEditor', { defaultValue: 'Open Editor' })}
          </Button>
        </div>

        {/* Founder governance sprint — rejected/returned state is explicit
            for the author, with the persisted rejection reason. */}
        {questionnaire.status === 'needs_revision' && (
          <Card className="p-4 border-destructive/40 bg-destructive/5" data-testid="studio-needs-revision-banner">
            <p className="text-sm font-semibold text-destructive flex items-center gap-1.5">
              <AlertTriangle className="h-4 w-4" />
              {t('studio.review.rejectedTitle', { defaultValue: 'Returned to author — changes required' })}
            </p>
            {(questionnaire.review_comments || []).length > 0 && (
              <div className="mt-2 text-sm">
                <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">
                  {t('studio.review.reason', { defaultValue: 'Reviewer comment' })}
                </p>
                <p className="whitespace-pre-wrap" data-testid="studio-rejection-reason">
                  {(questionnaire.review_comments[questionnaire.review_comments.length - 1] || {}).reason}
                </p>
                <p className="text-xs text-muted-foreground mt-1.5">
                  {t('studio.review.resubmitHint', { defaultValue: 'Correct the items below, then use "Send for review" to resubmit.' })}
                </p>
              </div>
            )}
          </Card>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="studio-blueprint-grid">
          {cards.map((c) => {
            const CardIcon = c.icon;
            return (
              <Card
                key={c.key}
                className="p-4 hover:shadow-md hover:border-primary/30 transition-all cursor-pointer"
                data-testid={`studio-blueprint-card-${c.key}`}
                onClick={onOpenEditor}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className="rounded-md bg-muted/60 p-1.5">
                    <CardIcon className="h-3.5 w-3.5 text-muted-foreground" />
                  </div>
                  <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                    {c.label}
                  </p>
                </div>
                <p className={`font-display text-base font-semibold ${c.multiline ? 'leading-snug line-clamp-3' : 'truncate'} ${c.tone ? '' : ''}`}>
                  {c.value}
                </p>
              </Card>
            );
          })}
          {/* Phase 2C.1 — Publish Readiness card */}
          <div data-testid="studio-blueprint-readiness-cell">
            <PublishReadinessChip
              envelope={validation?.envelope}
              loading={validation?.loading}
              variant="card"
              onClick={onOpenValidation}
            />
          </div>
        </div>

        <Card className="p-5 bg-muted/30" data-testid="studio-blueprint-summary-strip">
          <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">
            {t('studio.blueprint.liveSummary', { defaultValue: 'Live Assessment Summary' })}
          </p>
          <LiveSummaryStrip summary={summary} />
        </Card>

        {/* Founder governance sprint — editable classification metadata:
            Asset Type · Audience · Keywords · Author Notes · Publish Target.
            Correction batch §3-7: HM Admin may quick-edit these here at any
            lifecycle status via the audited governance path. */}
        <MetadataGovernanceCard
          questionnaire={questionnaire}
          meta={meta}
          editable={metaEditable ?? editable}
          patchMeta={governancePatch ?? patchMeta}
          isEs={isEs}
        />
      </div>
    </div>
  );
}

/* ============================================================================
 *      METADATA GOVERNANCE CARD (Founder sprint — Audience · Keywords ·
 *      Author Notes · Publish Target, all editable post-import)
 * ========================================================================== */

function MetadataGovernanceCard({ questionnaire, meta, editable, patchMeta, isEs }) {
  const { t } = useTranslation();
  const [kwInput, setKwInput] = useState('');
  const keywords = questionnaire.keywords || [];
  const targets = meta?.publish_targets || [];

  const addKeyword = () => {
    const v = kwInput.trim();
    if (!v) return;
    if (!keywords.includes(v)) patchMeta({ keywords: [...keywords, v] });
    setKwInput('');
  };
  const removeKeyword = (kw) => patchMeta({ keywords: keywords.filter((k) => k !== kw) });

  return (
    <Card className="p-5" data-testid="studio-metadata-governance-card">
      <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-3">
        {t('studio.metaCard.title', { defaultValue: 'Classification & Governance Metadata' })}
      </p>
      <div className="grid md:grid-cols-2 gap-5">
        <div className="space-y-1.5">
          <Label className="text-sm">{t('studio.metaCard.assetType', { defaultValue: 'Asset Type' })}</Label>
          <Select value={questionnaire.type || ''} disabled={!editable}
                  onValueChange={(v) => patchMeta({ type: v })}>
            <SelectTrigger data-testid="studio-meta-asset-type">
              <SelectValue placeholder={t('studio.metaCard.assetTypePh', { defaultValue: 'Select asset type…' })} />
            </SelectTrigger>
            <SelectContent>
              {(meta?.asset_types || []).map((a) => (
                <SelectItem key={a.code} value={a.code}>{isEs ? a.label_es : a.label_en}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            {t('studio.metaCard.assetTypeHelp', { defaultValue: 'Governance classification — never affects scoring or content.' })}
          </p>
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm">{t('studio.metaCard.audience', { defaultValue: 'Audience' })}</Label>
          <Textarea rows={2} value={questionnaire.audience || ''} disabled={!editable}
                    onChange={(e) => patchMeta({ audience: e.target.value })}
                    placeholder={t('studio.metaCard.audiencePh', { defaultValue: 'e.g. Collaborators who hold or aspire to leadership or supervisory positions.' })}
                    data-testid="studio-meta-audience" />
          <p className="text-xs text-muted-foreground">
            {t('studio.metaCard.audienceHelp', { defaultValue: 'Describe the intended TARGET POPULATION as a sentence — not a keyword or competency (use Keywords for search terms).' })}
          </p>
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm">{t('studio.metaCard.publishTarget', { defaultValue: 'Publish Target' })}</Label>
          <Select value={questionnaire.publish_target || ''} disabled={!editable}
                  onValueChange={(v) => patchMeta({ publish_target: v })}>
            <SelectTrigger data-testid="studio-meta-publish-target">
              <SelectValue placeholder={t('studio.metaCard.publishTargetPh', { defaultValue: 'Select destination…' })} />
            </SelectTrigger>
            <SelectContent>
              {targets.map((p) => (
                <SelectItem key={p.value} value={p.value}>{isEs ? p.label_es : p.label_en}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            {t('studio.metaCard.publishTargetHelp', { defaultValue: 'Required before sending for review or publishing. Determines which suite receives this instrument.' })}
          </p>
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm">{t('studio.metaCard.keywords', { defaultValue: 'Keywords' })}</Label>
          <div className="flex gap-2">
            <Input value={kwInput} disabled={!editable}
                   onChange={(e) => setKwInput(e.target.value)}
                   onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addKeyword(); } }}
                   placeholder={t('studio.metaCard.keywordsPh', { defaultValue: 'Type a keyword and press Enter' })}
                   data-testid="studio-meta-keyword-input" />
            <Button type="button" variant="outline" size="sm" className="shrink-0" disabled={!editable}
                    onClick={addKeyword} data-testid="studio-meta-keyword-add">
              <Plus className="h-3.5 w-3.5" />
            </Button>
          </div>
          {keywords.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-1" data-testid="studio-meta-keywords-list">
              {keywords.map((kw) => (
                <Badge key={kw} variant="outline" className="gap-1 pr-1">
                  {kw}
                  {editable && (
                    <button type="button" onClick={() => removeKeyword(kw)} className="hover:text-destructive"
                            data-testid={`studio-meta-keyword-remove-${kw}`}>
                      ×
                    </button>
                  )}
                </Badge>
              ))}
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            {t('studio.metaCard.keywordsHelp', { defaultValue: 'Search / classification terms (ES + EN welcome). Never affects scoring.' })}
          </p>
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm">{t('studio.metaCard.authorNotes', { defaultValue: 'Author Notes' })}</Label>
          <Textarea rows={2} value={questionnaire.author_notes || ''} disabled={!editable}
                    onChange={(e) => patchMeta({ author_notes: e.target.value })}
                    placeholder={t('studio.metaCard.authorNotesPh', { defaultValue: 'Internal context for reviewers and future maintainers…' })}
                    data-testid="studio-meta-author-notes" />
          <p className="text-xs text-muted-foreground">
            {t('studio.metaCard.authorNotesHelp', { defaultValue: 'Visible to authors, HM Advisors and HM Admin — never shown to evaluated users.' })}
          </p>
        </div>
      </div>
    </Card>
  );
}

/* ============================================================================
 *                          THREE-PANE EDITOR
 * ========================================================================== */

function EditorThreePane({ questionnaire, sections, questions, assetTypeMeta, meta, summary, editable, focus, setFocus, patchMeta, patchSections, patchQuestions, onReorderSections, onReorderQuestion, onBackToBlueprint, contentStatus, sectionStatus, onBulkApplied, inspectorTabRequest, onOpenFindReplace, validationEnvelope, onOpenValidation }) {
  const { t } = useTranslation();
  // Package B — feed the governed competency catalog into the bulk assign dialog.
  const [competencyCatalog, setCompetencyCatalog] = useState([]);
  useEffect(() => {
    let cancelled = false;
    adminApi.listCompetencies().then((rows) => {
      if (!cancelled) setCompetencyCatalog(Array.isArray(rows) ? rows : []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const [sidebarOpen] = useState(true);
  const [inspectorOpen] = useState(true);
  const [inspectorTab, setInspectorTab] = useState('overview');
  const [canvasMode, setCanvasMode] = useState('compose'); // 'compose' | 'bulk'

  // Auto-switch to "focus" tab only when the SELECTION changes — never when
  // the user manually picks a tab (UAT Finding #1: the old condition bounced
  // the Overview/Resumen tab straight back to Inspector while an item was
  // focused, making it look empty/dead).
  const prevFocusRef = useRef(focus);
  useEffect(() => {
    if (focus && focus !== prevFocusRef.current) setInspectorTab('focus');
    prevFocusRef.current = focus;
  }, [focus]);

  // Respond to an external request (e.g. validation issue clicked) to force-switch tabs.
  useEffect(() => {
    if (inspectorTabRequest) setInspectorTab(inspectorTabRequest);
  }, [inspectorTabRequest]);

  // UAT #1A — explicit affordance: focus a section AND open the Inspector tab
  // so the rename field is immediately visible.
  const handleInspectSection = (sid) => {
    setFocus({ kind: 'section', id: sid });
    setInspectorTab('focus');
    // A11 — the pencil affordance previously looked like a no-op: give an
    // explicit cue that editing happens in the Inspector panel.
    toast.info(t('studio.editor.sectionEditCue', { defaultValue: 'Section selected — edit its name and details in the Inspector panel on the right.' }));
  };

  const handleAddSection = () => {
    if (!editable) return;
    const newId = `s_${Date.now().toString(36)}`;
    const next = [...sections, { id: newId, name: t('pages.questionnaires.newSection', { defaultValue: 'New section' }), description: '', order: sections.length, weight: 1.0 }];
    patchSections(next);
    setFocus({ kind: 'section', id: newId });
  };
  const handleAddContent = (sectionId) => {
    if (!editable) return;
    const newId = `q_${Date.now().toString(36)}`;
    const order = questions.filter((q) => q.section_id === sectionId).length;
    const next = [...questions, {
      id: newId, section_id: sectionId, prompt: t('studio.editor.newContentItem', { defaultValue: 'New content item' }),
      type: 'single_choice', weight: 1.0, order, options: [], competency_map: {},
    }];
    patchQuestions(next);
    setFocus({ kind: 'content', id: newId });
  };
  const handleDeleteSection = (sid) => {
    if (!editable) return;
    if (!window.confirm(t('studio.editor.deleteSectionConfirm', { defaultValue: 'Delete this section and all its content?' }))) return;
    patchSections(sections.filter((s) => s.id !== sid));
    patchQuestions(questions.filter((q) => q.section_id !== sid));
    if (focus?.kind === 'section' && focus.id === sid) setFocus(null);
  };
  const handleDeleteContent = (qid) => {
    if (!editable) return;
    patchQuestions(questions.filter((q) => q.id !== qid));
    if (focus?.kind === 'content' && focus.id === qid) setFocus(null);
  };

  return (
    <>
      {sidebarOpen && (
        <aside
          className="w-80 border-r border-border bg-card flex flex-col overflow-hidden shrink-0"
          data-testid="studio-editor-sidebar"
        >
          <QuestionNavigator
            sections={sections}
            questions={questions}
            focus={focus}
            editable={editable}
            onFocus={setFocus}
            onAddSection={handleAddSection}
            onReorderSections={onReorderSections}
            onReorderQuestion={onReorderQuestion}
            onBackToBlueprint={onBackToBlueprint}
            contentStatus={contentStatus}
            sectionStatus={sectionStatus}
          />
          <Separator />
          <div className="px-3 py-3 bg-muted/20">
            <LiveSummaryStrip summary={summary} compact />
          </div>
        </aside>
      )}

      <main className="flex-1 overflow-y-auto flex flex-col" data-testid="studio-editor-canvas">
        <CanvasModeSwitch
          mode={canvasMode}
          onChange={setCanvasMode}
          questionCount={questions.length}
        />
        {canvasMode === 'compose' ? (
          <CanvasComposeMode
            sections={sections}
            questions={questions}
            focus={focus}
            editable={editable}
            onFocus={setFocus}
            onAddContent={handleAddContent}
            onDeleteSection={handleDeleteSection}
            onRenameSection={handleInspectSection}
          />
        ) : (
          <CanvasBulkMode
            sections={sections}
            questions={questions}
            editable={editable}
            patchQuestions={patchQuestions}
            competencies={competencyCatalog.length ? competencyCatalog : (meta?.competencies || [])}
            availableTags={[]}
            onBulkApplied={onBulkApplied}
            onOpenFindReplace={onOpenFindReplace}
          />
        )}
      </main>

      {inspectorOpen && (
        <aside
          className="w-96 border-l border-border bg-card overflow-y-auto shrink-0"
          data-testid="studio-editor-inspector"
        >
          <InspectorPanel
            questionnaire={questionnaire}
            assetTypeMeta={assetTypeMeta}
            meta={meta}
            focus={focus}
            sections={sections}
            questions={questions}
            summary={summary}
            editable={editable}
            patchMeta={patchMeta}
            patchSections={patchSections}
            patchQuestions={patchQuestions}
            onDeleteContent={handleDeleteContent}
            activeTab={inspectorTab}
            onTabChange={setInspectorTab}
            validationEnvelope={validationEnvelope}
            onOpenValidation={onOpenValidation}
          />
        </aside>
      )}
    </>
  );
}

/* ============================================================================
 *                          SIDEBAR OUTLINE
 * ========================================================================== */

/* ============================================================================
 *                          CANVAS MODE SWITCH (Phase 2B.3)
 * ========================================================================== */

function CanvasModeSwitch({ mode, onChange, questionCount }) {
  const { t } = useTranslation();
  return (
    <div
      className="border-b border-border bg-card/60 px-4 py-2 flex items-center gap-2 shrink-0"
      data-testid="studio-canvas-mode-switch"
    >
      <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mr-2">
        {t('studio.canvasMode.label', { defaultValue: 'Canvas mode' })}
      </p>
      <div className="inline-flex rounded-md border border-border overflow-hidden" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'compose'}
          onClick={() => onChange('compose')}
          data-testid="studio-canvas-mode-compose"
          className={[
            'h-7 px-3 text-xs inline-flex items-center gap-1.5 transition-colors',
            mode === 'compose' ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted/60',
          ].join(' ')}
        >
          <LayoutList className="h-3 w-3" />
          {t('studio.canvasMode.compose', { defaultValue: 'Compose' })}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'bulk'}
          onClick={() => onChange('bulk')}
          data-testid="studio-canvas-mode-bulk"
          className={[
            'h-7 px-3 text-xs inline-flex items-center gap-1.5 transition-colors border-l border-border',
            mode === 'bulk' ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted/60',
          ].join(' ')}
        >
          <TableIcon className="h-3 w-3" />
          {t('studio.canvasMode.bulk', { defaultValue: 'Bulk' })}
          {questionCount > 0 && (
            <Badge variant="outline" className="text-xs ml-1 h-4 px-1">
              {questionCount}
            </Badge>
          )}
        </button>
      </div>
      {mode === 'bulk' && (
        <span className="text-xs text-muted-foreground ml-2" data-testid="studio-canvas-mode-hint">
          {t('studio.canvasMode.bulkHint', {
            defaultValue: 'Multi-select content items to apply Weight · Difficulty · Competency · Move · Tags · Duplicate · Delete in one save.',
          })}
        </span>
      )}
    </div>
  );
}

/* ============================================================================
 *                          CANVAS — COMPOSE MODE (read-only)
 * ========================================================================== */

function CanvasComposeMode({ sections, questions, focus, editable, onFocus, onAddContent, onDeleteSection, onRenameSection }) {
  const { t } = useTranslation();
  if (sections.length === 0) {
    return (
      <div className="p-12 text-center" data-testid="studio-canvas-empty">
        <FileText className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
        <p className="text-sm text-muted-foreground">
          {editable
            ? t('studio.editor.canvasEmptyEditable', { defaultValue: 'No sections yet. Use "+ Section" on the left to start.' })
            : t('studio.editor.canvasEmpty', { defaultValue: 'No sections yet.' })}
        </p>
      </div>
    );
  }
  return (
    <div className="max-w-4xl mx-auto p-6 space-y-4">
      {sections.map((s, sIdx) => {
        const sectionQs = questions.filter((q) => q.section_id === s.id)
                                   .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        const sectionSelected = focus?.kind === 'section' && focus?.id === s.id;
        return (
          <Card
            key={s.id}
            className={`p-5 ${sectionSelected ? 'ring-2 ring-primary/40' : ''}`}
            data-testid={`studio-canvas-section-${s.id}`}
            onClick={(e) => { if (e.target === e.currentTarget) onFocus({ kind: 'section', id: s.id }); }}
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="min-w-0 flex-1" onClick={() => onFocus({ kind: 'section', id: s.id })}>
                <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                  {t('studio.editor.sectionLabel', { defaultValue: 'Section {{n}}', n: sIdx + 1 })}
                </p>
                <h2 className="font-display text-lg font-bold tracking-tight">
                  {s.name || t('studio.editor.untitledSection', { defaultValue: 'Untitled section' })}
                </h2>
                {s.description && <p className="text-xs text-muted-foreground mt-1 max-w-2xl">{s.description}</p>}
              </div>
              <Badge variant="outline" className="text-xs shrink-0">
                {sectionQs.length} {t('studio.editor.contentItems', { defaultValue: 'content items' })}
              </Badge>
              {editable && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground shrink-0"
                  onClick={(e) => { e.stopPropagation(); onRenameSection(s.id); }}
                  data-testid={`studio-canvas-rename-section-${s.id}`}
                  title={t('studio.editor.renameSection', { defaultValue: 'Rename section' })}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              )}
              {editable && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive shrink-0"
                  onClick={(e) => { e.stopPropagation(); onDeleteSection(s.id); }}
                  data-testid={`studio-canvas-delete-section-${s.id}`}
                  title={t('studio.editor.deleteSection', { defaultValue: 'Delete section' })}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
            <Separator />
            <ul className="mt-4 space-y-3" data-testid={`studio-canvas-content-list-${s.id}`}>
              {sectionQs.map((q, qIdx) => {
                const selected = focus?.kind === 'content' && focus?.id === q.id;
                return (
                  <li key={q.id}>
                    <button
                      type="button"
                      onClick={() => onFocus({ kind: 'content', id: q.id })}
                      data-testid={`studio-canvas-content-${q.id}`}
                      className={`w-full text-left rounded-md border-2 p-3 transition-all ${
                        selected ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/30'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <span className="text-xs font-mono text-muted-foreground shrink-0 mt-0.5">
                          Q{sIdx + 1}.{qIdx + 1}
                        </span>
                        {q.stable_id && (
                          <span
                            className="text-xs font-mono px-1.5 py-0.5 rounded bg-muted text-muted-foreground shrink-0 mt-0.5"
                            data-testid={`studio-canvas-stable-id-${q.id}`}
                            title="Permanent Content ID (stable across versions)"
                          >
                            {q.stable_id}
                          </span>
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium leading-snug">
                            {q.prompt || t('studio.editor.untitledQuestion', { defaultValue: 'Untitled content' })}
                          </p>
                          <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                            <Badge variant="outline" className="text-xs">{t(`studio.contentTypes.${q.type || 'text'}`, { defaultValue: q.type || 'text' })}</Badge>
                            {typeof q.weight === 'number' && <Badge variant="outline" className="text-xs">w {q.weight}</Badge>}
                            {Object.keys(q.competency_map || {}).length > 0 && (
                              <Badge variant="outline" className="text-xs">
                                {Object.keys(q.competency_map).length} comp
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
            {editable && (
              <Button
                variant="ghost"
                size="sm"
                className="mt-3 w-full text-muted-foreground hover:text-foreground border border-dashed"
                onClick={() => onAddContent(s.id)}
                data-testid={`studio-canvas-add-content-${s.id}`}
              >
                <Plus className="h-3.5 w-3.5 mr-1.5" />
                {t('studio.editor.addContent', { defaultValue: 'Add content item' })}
              </Button>
            )}
          </Card>
        );
      })}
    </div>
  );
}

/* ============================================================================
 *                          INSPECTOR (right pane)
 * ========================================================================== */

function InspectorPanel({ questionnaire, assetTypeMeta, meta, focus, sections, questions, summary, editable, patchMeta, patchSections, patchQuestions, onDeleteContent, activeTab, onTabChange, validationEnvelope, onOpenValidation }) {
  const { t } = useTranslation();

  return (
    <Tabs value={activeTab} onValueChange={onTabChange} className="h-full flex flex-col" data-testid="studio-inspector-tabs">
      <div className="px-3 pt-3">
        <TabsList className="grid grid-cols-3 w-full">
          <TabsTrigger value="overview" data-testid="studio-inspector-tab-overview">
            {t('studio.inspector.overview', { defaultValue: 'Overview' })}
          </TabsTrigger>
          <TabsTrigger value="focus" data-testid="studio-inspector-tab-focus">
            {t('studio.inspector.focus', { defaultValue: 'Inspector' })}
          </TabsTrigger>
          <TabsTrigger value="recommendations" data-testid="studio-inspector-tab-recommendations">
            <Sparkles className="h-3 w-3 mr-1" />
            {t('studio.inspector.recommendations', { defaultValue: 'Tips' })}
          </TabsTrigger>
        </TabsList>
      </div>

      <TabsContent value="overview" className="flex-1 overflow-y-auto px-4 py-3 mt-0">
        <OverviewInspector
          questionnaire={questionnaire}
          assetTypeMeta={assetTypeMeta}
          meta={meta}
          summary={summary}
          editable={editable}
          patchMeta={patchMeta}
          questions={questions}
        />
      </TabsContent>
      <TabsContent value="focus" className="flex-1 overflow-y-auto px-4 py-3 mt-0">
        <FocusInspector
          focus={focus}
          sections={sections}
          questions={questions}
          editable={editable}
          patchSections={patchSections}
          patchQuestions={patchQuestions}
          onDeleteContent={onDeleteContent}
        />
      </TabsContent>
      <TabsContent value="recommendations" className="flex-1 overflow-y-auto px-4 py-3 mt-0">
        <RecommendationsInspector questionnaire={questionnaire} assetTypeMeta={assetTypeMeta} sections={sections} questions={questions} summary={summary} validationEnvelope={validationEnvelope} onOpenValidation={onOpenValidation} />
      </TabsContent>
    </Tabs>
  );
}

function OverviewInspector({ questionnaire, assetTypeMeta, meta, summary, editable, patchMeta, questions = [] }) {
  const { t, i18n } = useTranslation();
  const isEs = i18n.language?.startsWith('es');
  const cap = assetTypeMeta?.capabilities || questionnaire.capabilities || {};
  const requiredFields = new Set(Object.entries(cap).filter(([, v]) => v === 'required').map(([k]) => k));

  if (!editable) {
    // Read-only mirror of Phase 2A.
    const rows = [
      { k: t('studio.blueprint.assetType', { defaultValue: 'Asset Type' }), v: assetTypeMeta ? (isEs ? assetTypeMeta.label_es : assetTypeMeta.label_en) : (questionnaire.type || '—') },
      ...(cap.difficulty === 'hidden' ? [] : [
        { k: t('studio.blueprint.difficulty', { defaultValue: 'Difficulty' }), v: `D${summary.difficulty} · ${difficultyLabel(t, summary.difficulty)}` },
      ]),
      { k: t('studio.blueprint.estimatedTime', { defaultValue: 'Estimated Time' }), v: summary.minutes ? `${summary.minutes} min` : '—' },
      ...(cap.passing_score === 'hidden' ? [] : [
        { k: t('studio.blueprint.passingScore', { defaultValue: 'Passing Score' }), v: questionnaire.passing_score != null ? `${questionnaire.passing_score}%` : '—' },
      ]),
      { k: t('studio.blueprint.publishTarget', { defaultValue: 'Publish Target' }), v: (() => {
        const m = meta?.publish_targets?.find((p) => p.value === (questionnaire.publish_target || questionnaire.publish_target_suggestion));
        if (!m) return '—';
        const label = isEs ? m.label_es : m.label_en;
        return questionnaire.publish_target ? label : `${t('studio.blueprint.suggestedPrefix', { defaultValue: 'Suggested' })}: ${label}`;
      })() },
    ];
    return (
      <div className="space-y-3" data-testid="studio-inspector-overview-panel">
        <div className="space-y-1.5">
          {rows.map((r) => (
            <div key={r.k} className="flex justify-between text-xs">
              <span className="text-muted-foreground">{r.k}</span>
              <span className="font-medium text-right truncate ml-2">{r.v}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="studio-inspector-overview-panel">
      <EditableField
        label={t('studio.inspector.assetName', { defaultValue: 'Name' })}
        value={questionnaire.name || ''}
        onCommit={(v) => patchMeta({ name: v })}
        testid="studio-inspector-meta-name"
      />
      <EditableField
        label={t('studio.inspector.description', { defaultValue: 'Description' })}
        value={questionnaire.description || ''}
        onCommit={(v) => patchMeta({ description: v })}
        multiline
        testid="studio-inspector-meta-description"
      />
      <EditableField
        label={t('studio.blueprint.purpose', { defaultValue: 'Purpose' })}
        value={questionnaire.objective || ''}
        onCommit={(v) => patchMeta({ objective: v })}
        multiline
        testid="studio-inspector-meta-objective"
        required={requiredFields.has('objective')}
      />
      <EditableField
        label={t('studio.blueprint.audience', { defaultValue: 'Audience' })}
        value={questionnaire.professional_area || ''}
        onCommit={(v) => patchMeta({ professional_area: v })}
        testid="studio-inspector-meta-audience"
      />
      {cap.difficulty !== 'hidden' && (
      <div>
        <Label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
          {t('studio.blueprint.difficulty', { defaultValue: 'Difficulty' })}
        </Label>
        <div className="mt-1.5">
          <DifficultyPicker
            value={questionnaire.difficulty ?? null}
            onChange={(v) => patchMeta({ difficulty: v })}
          />
        </div>
      </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <EditableField
            label={t('studio.blueprint.estimatedTime', { defaultValue: 'Estimated Time' })}
            value={questionnaire.estimated_minutes ?? ''}
            onCommit={(v) => patchMeta({ estimated_minutes: v === '' ? null : Number(v) })}
            type="number"
            testid="studio-inspector-meta-minutes"
            required={requiredFields.has('estimated_time')}
          />
          {/* A10 — make the auto heuristic vs author value distinction explicit. */}
          <p className="text-[11px] text-muted-foreground mt-1" data-testid="studio-minutes-auto-hint">
            {questionnaire.estimated_minutes == null
              ? t('studio.inspector.minutesAutoHint', { defaultValue: 'Not set — footer shows the automatic estimate (~{{n}} min).', n: estimateMinutes(questions) })
              : t('studio.inspector.minutesManualHint', { defaultValue: 'Author-defined. Automatic estimate: ~{{n}} min.', n: estimateMinutes(questions) })}
            {questionnaire.estimated_minutes == null && (
              <button type="button" className="text-primary hover:underline ml-1"
                onClick={() => patchMeta({ estimated_minutes: estimateMinutes(questions) })}
                data-testid="studio-minutes-use-auto">
                {t('studio.inspector.minutesUseAuto', { defaultValue: 'Use automatic estimate' })}
              </button>
            )}
          </p>
        </div>
        {cap.passing_score !== 'hidden' && (
        <EditableField
          label={t('studio.blueprint.passingScore', { defaultValue: 'Passing Score (%)' })}
          value={questionnaire.passing_score ?? ''}
          onCommit={(v) => patchMeta({ passing_score: v === '' ? null : Number(v) })}
          type="number"
          testid="studio-inspector-meta-passing"
          required={requiredFields.has('passing_score')}
        />
        )}
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
          {t('studio.blueprint.publishTarget', { defaultValue: 'Publish Target' })}
        </Label>
        <Select
          value={questionnaire.publish_target || ''}
          onValueChange={(v) => patchMeta({ publish_target: v })}
        >
          <SelectTrigger data-testid="studio-inspector-meta-publish-target" className="mt-1.5">
            <SelectValue placeholder={(() => {
              const sug = meta?.publish_targets?.find((p) => p.value === questionnaire.publish_target_suggestion);
              return sug
                ? `${t('studio.blueprint.suggestedPrefix', { defaultValue: 'Suggested' })}: ${isEs ? sug.label_es : sug.label_en}`
                : t('studio.lifecycle.pickPublishTarget', { defaultValue: 'Select destination…' });
            })()} />
          </SelectTrigger>
          <SelectContent>
            {(meta?.publish_targets || []).map((p) => (
              <SelectItem key={p.value} value={p.value}>
                {isEs ? p.label_es : p.label_en}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Separator />
      <div>
        <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">
          {t('studio.inspector.capabilities', { defaultValue: 'Capabilities' })}
        </p>
        <div className="flex flex-wrap gap-1">
          {Object.entries(cap).filter(([, v]) => v && v !== 'hidden').map(([k, v]) => (
            <Badge key={k} variant="outline" className="text-xs" title={`${k} · ${v}`}>
              {t(`studio.capabilities.${k}`, { defaultValue: k.replace(/_/g, ' ') })} · {t(`studio.capabilities.values.${v}`, { defaultValue: String(v) })}
            </Badge>
          ))}
        </div>
      </div>
    </div>
  );
}

function FocusInspector({ focus, sections, questions, editable, patchSections, patchQuestions, onDeleteContent }) {
  const { t } = useTranslation();
  if (!focus) {
    return (
      <div className="text-center py-8" data-testid="studio-inspector-focus-empty">
        <ListChecks className="h-8 w-8 mx-auto text-muted-foreground/40 mb-2" />
        <p className="text-xs text-muted-foreground">
          {t('studio.inspector.focusEmpty', { defaultValue: 'Click a section or content item to inspect it.' })}
        </p>
      </div>
    );
  }
  if (focus.kind === 'section') {
    const s = sections.find((x) => x.id === focus.id);
    if (!s) return null;
    const updateSection = (patch) => {
      patchSections(sections.map((x) => (x.id === s.id ? { ...x, ...patch } : x)));
    };
    return (
      <div className="space-y-3" data-testid="studio-inspector-section-panel">
        <EditableField
          label={t('studio.inspector.sectionName', { defaultValue: 'Name' })}
          value={s.name || ''}
          onCommit={(v) => updateSection({ name: v })}
          disabled={!editable}
          testid="studio-inspector-section-name"
        />
        <EditableField
          label={t('studio.inspector.description', { defaultValue: 'Description' })}
          value={s.description || ''}
          onCommit={(v) => updateSection({ description: v })}
          multiline
          disabled={!editable}
          testid="studio-inspector-section-description"
        />
        <EditableField
          label={t('studio.inspector.weight', { defaultValue: 'Weight' })}
          value={s.weight ?? ''}
          onCommit={(v) => updateSection({ weight: v === '' ? null : Number(v) })}
          type="number"
          disabled={!editable}
          testid="studio-inspector-section-weight"
        />
      </div>
    );
  }
  const q = questions.find((x) => x.id === focus.id);
  if (!q) return null;
  const updateQ = (patch) => {
    patchQuestions(questions.map((x) => (x.id === q.id ? { ...x, ...patch } : x)));
  };
  return (
    <div className="space-y-3" data-testid="studio-inspector-content-panel">
      <EditableField
        label={t('studio.inspector.prompt', { defaultValue: 'Prompt' })}
        value={q.prompt || ''}
        onCommit={(v) => updateQ({ prompt: v })}
        multiline
        disabled={!editable}
        testid="studio-inspector-content-prompt"
      />
      <div>
        <Label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
          {t('studio.inspector.contentType', { defaultValue: 'Content type' })}
        </Label>
        <Select
          value={q.type || 'text'}
          onValueChange={(v) => {
            // A1 — selecting Likert 5 auto-provisions exactly five localized
            // default anchors (scores 1..5) when the item has no valid set.
            if (v === 'likert_5' && (q.options || []).length !== 5) {
              updateQ({ type: v, options: likertDefaultAnchors() });
            } else {
              updateQ({ type: v });
            }
          }}
          disabled={!editable}
        >
          <SelectTrigger className="mt-1.5" data-testid="studio-inspector-content-type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="single_choice">{t('studio.contentTypes.single_choice', { defaultValue: 'Single choice' })}</SelectItem>
            <SelectItem value="multi_choice">{t('studio.contentTypes.multi_choice', { defaultValue: 'Multiple choice' })}</SelectItem>
            <SelectItem value="likert_5">{t('studio.contentTypes.likert_5', { defaultValue: 'Likert 5' })}</SelectItem>
            <SelectItem value="scale_1_10">{t('studio.contentTypes.scale_1_10', { defaultValue: 'Scale 1–10' })}</SelectItem>
            <SelectItem value="text">{t('studio.contentTypes.text', { defaultValue: 'Short text' })}</SelectItem>
            <SelectItem value="long_text">{t('studio.contentTypes.long_text', { defaultValue: 'Long text' })}</SelectItem>
            <SelectItem value="date">{t('studio.contentTypes.date', { defaultValue: 'Date' })}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <EditableField
        label={t('studio.inspector.weight', { defaultValue: 'Weight' })}
        value={q.weight ?? ''}
        onCommit={(v) => updateQ({ weight: v === '' ? null : Number(v) })}
        type="number"
        disabled={!editable}
        testid="studio-inspector-content-weight"
      />
      {['single_choice', 'multi_choice'].includes(q.type) ? (
        <OptionsEditor q={q} updateQ={updateQ} editable={editable} />
      ) : q.type === 'likert_5' ? (
        <LikertAnchorsEditor q={q} updateQ={updateQ} editable={editable} />
      ) : q.type === 'scale_1_10' ? (
        <ScaleAnchorsEditor q={q} updateQ={updateQ} editable={editable} />
      ) : (q.options || []).length > 0 && (
        <div>
          <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">
            {t('studio.inspector.options', { defaultValue: 'Options' })}
          </p>
          <ul className="space-y-1">
            {q.options.map((o, i) => (
              <li key={o.id || i} className="text-xs flex justify-between gap-2 border-b border-border/30 py-1">
                <span className="truncate">{o.label}</span>
                {typeof o.score === 'number' && <span className="text-muted-foreground tabular-nums">{o.score}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
      {Object.keys(q.competency_map || {}).length > 0 && (
        <CompetencyMapEditor q={q} updateQ={updateQ} editable={editable} />
      )}
      {editable && (
        <div className="pt-2">
          <Button
            variant="ghost"
            size="sm"
            className="w-full text-destructive hover:bg-destructive/10"
            onClick={() => onDeleteContent(q.id)}
            data-testid="studio-inspector-content-delete"
          >
            <Trash2 className="h-3.5 w-3.5 mr-1.5" />
            {t('studio.inspector.deleteContent', { defaultValue: 'Delete content item' })}
          </Button>
        </div>
      )}
    </div>
  );
}

// ---------- A1 — Likert 5 anchor editor ----------
// Exactly five anchors, scores fixed 1..5 (existing scoring engine contract:
// earned = selected option score / max option score). Labels editable ES + EN.
export function likertDefaultAnchors() {
  const es = ['Totalmente en desacuerdo', 'En desacuerdo', 'Neutral', 'De acuerdo', 'Totalmente de acuerdo'];
  const en = ['Strongly disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly agree'];
  return es.map((label, i) => ({
    id: `lk_${Date.now().toString(36)}${i}${Math.random().toString(36).slice(2, 5)}`,
    label, label_en: en[i], score: i + 1, weight: 1.0,
  }));
}

function LikertAnchorsEditor({ q, updateQ, editable }) {
  const { t } = useTranslation();
  const opts = q.options || [];
  const wrongCount = opts.length !== 5;
  const patch = (i, p) => updateQ({ options: opts.map((o, j) => (j === i ? { ...o, ...p } : o)) });
  return (
    <div data-testid="studio-likert-editor">
      <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">
        {t('studio.inspector.likertAnchors', { defaultValue: 'Likert anchors (5)' })}
      </p>
      {wrongCount ? (
        <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-500/10 p-2.5" data-testid="studio-likert-empty">
          <p className="text-xs text-amber-800 dark:text-amber-200 mb-2">
            {t('studio.inspector.likertWrongCount', { defaultValue: 'This item has {{n}} options — Likert 5 requires exactly 5 anchors.', n: opts.length })}
          </p>
          {editable && (
            <Button size="sm" variant="outline" className="h-7 text-xs"
              onClick={() => updateQ({ options: likertDefaultAnchors() })}
              data-testid="studio-likert-generate">
              {t('studio.inspector.likertGenerate', { defaultValue: 'Generate 5 default anchors' })}
            </Button>
          )}
        </div>
      ) : (
        <>
          <ul className="space-y-1.5">
            {opts.map((o, i) => (
              <li key={o.id || i} className="flex items-center gap-1.5" data-testid={`studio-likert-row-${i}`}>
                <span className="shrink-0 h-6 w-6 grid place-items-center rounded-full bg-muted text-xs font-bold tabular-nums" title={t('studio.inspector.likertScore', { defaultValue: 'Score (fixed)' })}>
                  {o.score ?? i + 1}
                </span>
                <Input
                  value={o.label || ''}
                  placeholder={t('studio.inspector.likertEsPh', { defaultValue: 'ES anchor {{n}}', n: i + 1 })}
                  onChange={(e) => patch(i, { label: e.target.value })}
                  disabled={!editable}
                  data-testid={`studio-likert-label-${i}`}
                  className="h-7 text-xs"
                />
                <Input
                  value={o.label_en || ''}
                  placeholder={t('studio.inspector.likertEnPh', { defaultValue: 'EN anchor {{n}}', n: i + 1 })}
                  onChange={(e) => patch(i, { label_en: e.target.value })}
                  disabled={!editable}
                  data-testid={`studio-likert-label-en-${i}`}
                  className="h-7 text-xs"
                />
              </li>
            ))}
          </ul>
          {editable && (
            <button type="button"
              className="text-xs text-primary hover:underline mt-1.5"
              onClick={() => updateQ({ options: likertDefaultAnchors() })}
              data-testid="studio-likert-reset">
              {t('studio.inspector.likertReset', { defaultValue: 'Restore default anchors' })}
            </button>
          )}
        </>
      )}
    </div>
  );
}

// ---------- A2 — Scale 1–10 anchor labels ----------
// Validator rule 16 reads q.scale_low_label / q.scale_high_label; the preview
// and PDF fall back to Bajo/Alto · Low/High when unset. This exposes the fields.
function ScaleAnchorsEditor({ q, updateQ, editable }) {
  const { t } = useTranslation();
  return (
    <div data-testid="studio-scale-anchors">
      <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">
        {t('studio.inspector.scaleAnchors', { defaultValue: 'Scale anchors (1–10)' })}
      </p>
      <div className="grid grid-cols-2 gap-2">
        <EditableField
          label={t('studio.inspector.scaleLow', { defaultValue: 'Low anchor (1)' })}
          value={q.scale_low_label || ''}
          onCommit={(v) => updateQ({ scale_low_label: v || null })}
          disabled={!editable}
          testid="studio-scale-low"
        />
        <EditableField
          label={t('studio.inspector.scaleHigh', { defaultValue: 'High anchor (10)' })}
          value={q.scale_high_label || ''}
          onCommit={(v) => updateQ({ scale_high_label: v || null })}
          disabled={!editable}
          testid="studio-scale-high"
        />
      </div>
      <p className="text-[11px] text-muted-foreground mt-1">
        {t('studio.inspector.scaleAnchorsHint', { defaultValue: 'Shown at both ends of the 1–10 scale. Defaults: Bajo/Alto · Low/High.' })}
      </p>
    </div>
  );
}

// ---------- A3 — existing competency mapping editor ----------
// Inspect / edit weight [0,1] / remove an existing mapping. Re-adding the same
// competency (e.g. via Bulk) overwrites the map key — idempotent by design.
function CompetencyMapEditor({ q, updateQ, editable }) {
  const { t } = useTranslation();
  const entries = Object.entries(q.competency_map || {});
  const setWeight = (code, v) => {
    const num = v === '' ? '' : Number(v);
    if (num === '' || Number.isNaN(num)) return;
    const clamped = Math.min(1, Math.max(0, num));
    updateQ({ competency_map: { ...q.competency_map, [code]: clamped } });
  };
  const remove = (code) => {
    const next = { ...q.competency_map };
    delete next[code];
    updateQ({ competency_map: next });
  };
  return (
    <div data-testid="studio-competency-map-editor">
      <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">
        {t('studio.inspector.competencies', { defaultValue: 'Competencies' })}
      </p>
      <ul className="space-y-1.5">
        {entries.map(([code, w]) => {
          const invalid = typeof w !== 'number' || w < 0 || w > 1;
          return (
            <li key={code} className="flex items-center gap-1.5" data-testid={`studio-compmap-row-${code}`}>
              <span className="flex-1 text-xs truncate" title={code}>
                {t(`reports.competencies.${code}`, { defaultValue: code })}
              </span>
              <Input
                type="number" min={0} max={1} step={0.05}
                value={w}
                onChange={(e) => setWeight(code, e.target.value)}
                disabled={!editable}
                data-testid={`studio-compmap-weight-${code}`}
                className={`h-7 w-20 text-xs tabular-nums ${invalid ? 'border-destructive' : ''}`}
              />
              {editable && (
                <button type="button" onClick={() => remove(code)}
                  data-testid={`studio-compmap-remove-${code}`}
                  title={t('studio.inspector.removeCompetency', { defaultValue: 'Remove mapping' })}
                  className="shrink-0 text-destructive/60 hover:text-destructive">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </li>
          );
        })}
      </ul>
      {entries.some(([, w]) => typeof w !== 'number' || w < 0 || w > 1) && (
        <p className="text-[11px] text-destructive mt-1" data-testid="studio-compmap-range-error">
          {t('studio.inspector.competencyRange', { defaultValue: 'Weights must be between 0 and 1.' })}
        </p>
      )}
    </div>
  );
}

// ---------- Answer-option editor for choice questions (UAT 07/17 — A) ----------
// Persists the same option shape used by the Library editor and the runner:
// { id, label, label_en, score, weight } — score > 0 marks the correct answer.
function OptionsEditor({ q, updateQ, editable }) {
  const { t } = useTranslation();
  const opts = q.options || [];
  const isSingle = q.type === 'single_choice';
  const set = (next) => updateQ({ options: next });
  const oid = () => `o_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
  const add = () => set([...opts, { id: oid(), label: '', label_en: null, score: 0, weight: 1.0 }]);
  const patch = (i, p) => set(opts.map((o, j) => (j === i ? { ...o, ...p } : o)));
  const remove = (i) => set(opts.filter((_, j) => j !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= opts.length) return;
    const next = [...opts];
    [next[i], next[j]] = [next[j], next[i]];
    set(next);
  };
  const markCorrect = (i) => {
    // UAT #1B — keep BOTH models in sync: the scoring engine reads `score`,
    // the validator/preview read `is_correct`.
    if (isSingle) {
      set(opts.map((o, j) => ({ ...o, score: j === i ? 1 : 0, is_correct: j === i })));
    } else {
      const nowCorrect = !((opts[i].score || 0) > 0);
      patch(i, { score: nowCorrect ? 1 : 0, is_correct: nowCorrect });
    }
  };
  return (
    <div data-testid="studio-options-editor">
      <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">
        {t('studio.inspector.options', { defaultValue: 'Options' })}
      </p>
      {opts.length === 0 && (
        <p className="text-xs text-muted-foreground mb-2" data-testid="studio-options-empty">
          {t('studio.inspector.optionsEmpty', { defaultValue: 'No answer options yet. Add at least 2.' })}
        </p>
      )}
      <ul className="space-y-1.5">
        {opts.map((o, i) => {
          const correct = (o.score || 0) > 0;
          return (
            <li key={o.id || i} className="flex items-center gap-1.5" data-testid={`studio-option-row-${i}`}>
              <button
                type="button"
                title={t('studio.inspector.markCorrect', { defaultValue: 'Mark as correct answer' })}
                onClick={() => editable && markCorrect(i)}
                disabled={!editable}
                data-testid={`studio-option-correct-${i}`}
                className={`shrink-0 rounded-full transition-colors ${correct ? 'text-emerald-600' : 'text-muted-foreground/40 hover:text-muted-foreground'}`}
              >
                {correct ? <CheckCircle2 className="h-4 w-4" /> : <Circle className="h-4 w-4" />}
              </button>
              <Input
                value={o.label || ''}
                placeholder={t('studio.inspector.optionLabelPh', { defaultValue: `Option ${i + 1}` })}
                onChange={(e) => patch(i, { label: e.target.value })}
                disabled={!editable}
                data-testid={`studio-option-label-${i}`}
                className="h-7 text-xs"
              />
              {editable && (
                <>
                  <button type="button" onClick={() => move(i, -1)} disabled={i === 0} data-testid={`studio-option-up-${i}`} className="shrink-0 text-muted-foreground/60 hover:text-foreground disabled:opacity-30">
                    <ChevronUp className="h-3.5 w-3.5" />
                  </button>
                  <button type="button" onClick={() => move(i, 1)} disabled={i === opts.length - 1} data-testid={`studio-option-down-${i}`} className="shrink-0 text-muted-foreground/60 hover:text-foreground disabled:opacity-30">
                    <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                  <button type="button" onClick={() => remove(i)} data-testid={`studio-option-remove-${i}`} className="shrink-0 text-destructive/60 hover:text-destructive">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </>
              )}
            </li>
          );
        })}
      </ul>
      {editable && (
        <Button
          variant="outline"
          size="sm"
          onClick={add}
          data-testid="studio-option-add"
          className="mt-2 h-7 text-xs gap-1 w-full"
        >
          <Plus className="h-3 w-3" />
          {t('studio.inspector.addOption', { defaultValue: 'Add option' })}
        </Button>
      )}
      <p className="text-xs text-muted-foreground mt-1.5">
        {isSingle
          ? t('studio.inspector.correctHintSingle', { defaultValue: 'The marked option scores 1 point (correct answer).' })
          : t('studio.inspector.correctHintMulti', { defaultValue: 'Each marked option scores 1 point.' })}
      </p>
    </div>
  );
}

// ---------- Inline editable field with debounced commit ----------
function EditableField({ label, value, onCommit, multiline, type, disabled, testid, required }) {
  const [local, setLocal] = useState(value ?? '');
  const localRef = useRef(local);
  const commitTimerRef = useRef(null);
  const focusedRef = useRef(false);
  useEffect(() => {
    if (focusedRef.current) return; // don't clobber text the user is typing
    setLocal(value ?? '');
    localRef.current = value ?? '';
  }, [value]);
  // UAT Finding #1 — never let typed-but-unblurred text sit uncommitted:
  // commit on a short debounce while typing so autosave always sees it.
  useEffect(() => () => { if (commitTimerRef.current) clearTimeout(commitTimerRef.current); }, []);

  const commitIfChanged = () => {
    if (localRef.current !== (value ?? '')) {
      onCommit(localRef.current);
    }
  };
  const handleBlur = () => {
    focusedRef.current = false;
    if (commitTimerRef.current) clearTimeout(commitTimerRef.current);
    commitIfChanged();
  };
  const handleFocus = () => { focusedRef.current = true; };
  const handleChange = (e) => {
    const v = type === 'number' ? e.target.value : e.target.value;
    setLocal(v);
    localRef.current = v;
    if (commitTimerRef.current) clearTimeout(commitTimerRef.current);
    commitTimerRef.current = setTimeout(commitIfChanged, 800);
  };
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
        {label}{required && <span className="text-destructive ml-0.5">*</span>}
      </Label>
      {multiline ? (
        <Textarea
          value={local}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          data-testid={testid}
          className="mt-1.5 text-sm"
          rows={3}
        />
      ) : (
        <Input
          type={type || 'text'}
          value={local}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          data-testid={testid}
          className="mt-1.5"
        />
      )}
      {required && !local && (
        <p className="text-xs text-destructive mt-0.5">
          Required by asset type
        </p>
      )}
    </div>
  );
}

function Field({ label, value, multiline }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-0.5">{label}</p>
      <p className={`text-sm ${multiline ? '' : 'truncate'}`}>{value || '—'}</p>
    </div>
  );
}

function RecommendationsInspector({ questionnaire, assetTypeMeta, sections, questions, summary, validationEnvelope, onOpenValidation }) {
  const { t } = useTranslation();
  const recs = useMemo(
    () => computeRecommendations(questionnaire, assetTypeMeta, sections, questions, summary, t),
    [questionnaire, assetTypeMeta, sections, questions, summary],
  );
  const { error: valErrors = 0, warning: valWarnings = 0 } = validationEnvelope?.severity_counts || {};
  const validationHasIssues = valErrors + valWarnings > 0;
  if (recs.length === 0) {
    // Only claim "healthy" when the Validation Center agrees. If the validator
    // reports open errors/warnings, surface that truth instead (CORE walkthrough fix).
    if (validationHasIssues) {
      return (
        <div className="text-center py-8" data-testid="studio-recommendations-validation-pending">
          <AlertTriangle className="h-8 w-8 mx-auto text-amber-500/80 mb-2" />
          <p className="text-xs text-muted-foreground px-3">
            {t('studio.recommendations.validationPending', {
              defaultValue: 'The Validation Center reports {{e}} error(s) and {{w}} warning(s). Resolve them before publishing.',
              e: valErrors, w: valWarnings,
            })}
          </p>
          {onOpenValidation && (
            <Button variant="outline" size="sm" className="mt-3 h-7 text-xs" onClick={onOpenValidation} data-testid="studio-recommendations-open-validation">
              {t('studio.recommendations.openValidation', { defaultValue: 'Open Validation Center' })}
            </Button>
          )}
        </div>
      );
    }
    return (
      <div className="text-center py-8" data-testid="studio-recommendations-empty">
        <CheckCircle2 className="h-8 w-8 mx-auto text-success/70 mb-2" />
        <p className="text-xs text-muted-foreground">
          {t('studio.recommendations.empty', { defaultValue: 'No recommendations right now. This assessment looks healthy.' })}
        </p>
      </div>
    );
  }
  return (
    <ul className="space-y-2" data-testid="studio-recommendations-list">
      {recs.map((r) => (
        <li key={r.id}>
          <Card className="p-3 border-l-4" style={{ borderLeftColor: r.severity === 'warning' ? '#f59e0b' : '#3b82f6' }} data-testid={`studio-rec-${r.id}`}>
            <div className="flex items-start gap-2">
              {r.severity === 'warning' ? (
                <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
              ) : (
                <Sparkles className="h-4 w-4 text-primary shrink-0 mt-0.5" />
              )}
              <div className="min-w-0">
                <p className="text-xs font-semibold mb-0.5">{r.title}</p>
                <p className="text-xs text-muted-foreground leading-snug">{r.message}</p>
                <p className="text-xs text-muted-foreground/70 mt-1.5 italic">
                  {t('studio.recommendations.family', { defaultValue: 'Family' })}: {r.family}
                </p>
              </div>
            </div>
          </Card>
        </li>
      ))}
    </ul>
  );
}

/* ============================================================================
 *                          LIVE SUMMARY (F3)
 * ========================================================================== */

function LiveSummaryStrip({ summary, compact }) {
  const { t } = useTranslation();
  const cells = [
    { k: 'sections', label: t('studio.summary.sections', { defaultValue: 'Sections' }), value: summary.sections },
    { k: 'content', label: t('studio.summary.content', { defaultValue: 'Content' }), value: summary.content },
    { k: 'competencies', label: t('studio.summary.competencies', { defaultValue: 'Competencies' }), value: summary.competencies },
    { k: 'minutes', label: t('studio.summary.minutes', { defaultValue: 'Est. min' }), value: summary.minutes || '—' },
    ...(summary.scoringNa ? [] : [
      { k: 'difficulty', label: t('studio.summary.difficulty', { defaultValue: 'Difficulty' }), value: `D${summary.difficulty}` },
    ]),
  ];
  const healthBg = summary.health === 'healthy' ? 'bg-success/10 text-success' :
                   summary.health === 'warning' ? 'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-200' :
                   'bg-destructive/10 text-destructive';
  return (
    <div className={`grid ${compact ? 'grid-cols-2 gap-y-2' : 'grid-cols-7'} gap-2`} data-testid="studio-live-summary">
      {cells.map((c) => (
        <div key={c.k} data-testid={`studio-summary-${c.k}`}>
          <p className="text-xs uppercase tracking-wider text-muted-foreground">{c.label}</p>
          <p className="font-display text-base font-bold tabular-nums leading-tight">{c.value}</p>
        </div>
      ))}
      <div data-testid="studio-summary-health">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">
          {t('studio.summary.health', { defaultValue: 'Health' })}
        </p>
        <span className={`inline-block text-xs font-bold px-1.5 py-0.5 rounded mt-0.5 ${healthBg}`}>
          {summary.health === 'healthy' && '🟢'}
          {summary.health === 'warning' && '🟡'}
          {summary.health === 'critical' && '🔴'}
          {' '}
          {t(`studio.summary.health_${summary.health}`, { defaultValue: summary.health })}
        </span>
      </div>
      {/* A9 — publication readiness (canonical: Validation Center) shown apart
          from structural health so a blocked asset never looks publish-ready. */}
      <div data-testid="studio-summary-readiness">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">
          {t('studio.summary.readiness', { defaultValue: 'Publication' })}
        </p>
        {summary.valErrors == null ? (
          <span className="inline-block text-xs font-bold px-1.5 py-0.5 rounded mt-0.5 bg-muted text-muted-foreground" data-testid="studio-readiness-unknown">
            {t('studio.summary.readinessUnknown', { defaultValue: 'Not validated' })}
          </span>
        ) : summary.valErrors > 0 ? (
          <span className="inline-block text-xs font-bold px-1.5 py-0.5 rounded mt-0.5 bg-destructive/10 text-destructive" data-testid="studio-readiness-blocked">
            ⛔ {t('studio.summary.readinessBlocked', { defaultValue: 'Not ready · {{n}} error(s)', n: summary.valErrors })}
          </span>
        ) : (
          <span className="inline-block text-xs font-bold px-1.5 py-0.5 rounded mt-0.5 bg-success/10 text-success" data-testid="studio-readiness-ready">
            ✓ {t('studio.summary.readinessReady', { defaultValue: 'Ready to publish' })}
          </span>
        )}
      </div>
    </div>
  );
}

/* ============================================================================
 *                          STATUS BAR (bottom)
 * ========================================================================== */

function EditorStatusBar({ summary, sessionStartedAt }) {
  const { t } = useTranslation();
  return (
    <footer
      className="h-8 border-t border-border bg-card px-4 flex items-center justify-between text-xs text-muted-foreground shrink-0"
      data-testid="studio-editor-statusbar"
    >
      <span>
        {summary.scoringNa
          ? t('studio.status.summaryNoScore', {
              defaultValue: '{{sections}} Sections · {{content}} Content items · ~{{minutes}} min',
              sections: summary.sections,
              content: summary.content,
              minutes: summary.minutes || '—',
            })
          : t('studio.status.summary', {
              defaultValue: '{{sections}} Sections · {{content}} Content items · {{competencies}} Competencies · ~{{minutes}} min · D{{difficulty}}',
              sections: summary.sections,
              content: summary.content,
              competencies: summary.competencies,
              minutes: summary.minutes || '—',
              difficulty: summary.difficulty,
            })}
      </span>
      <SessionTimer startedAt={sessionStartedAt} />
    </footer>
  );
}

/* ============================================================================
 *                          PURE HELPERS
 * ========================================================================== */

function FullScreen({ children }) {
  return (
    <div className="h-screen flex flex-col items-center justify-center bg-background">
      {children}
    </div>
  );
}

function mergeLatest(versions, latest) {
  if (!latest) return versions;
  if (!versions?.length) return [latest];
  const next = versions.slice();
  const idx = next.findIndex((v) => v.version_number === latest.version_number);
  if (idx >= 0) next[idx] = { ...next[idx], ...latest };
  else next.push(latest);
  return next;
}

function estimateMinutes(questions) {
  // Rule-of-thumb: 30s per single_choice/likert; 45s for multi; 90s for text; 180s for long_text.
  const perType = { single_choice: 0.5, multi_choice: 0.75, likert_5: 0.5, scale_1_10: 0.5, text: 1.5, long_text: 3, date: 0.5 };
  const total = questions.reduce((acc, q) => acc + (perType[q.type] ?? 1), 0);
  return Math.max(1, Math.round(total));
}

function deriveHealth(questionnaire, sections, questions) {
  if (sections.length === 0) return 'critical';
  if (questions.length === 0) return 'critical';
  // Required-by-capability checks.
  const cap = questionnaire.capabilities || {};
  if (cap.passing_score === 'required' && questionnaire.passing_score == null) return 'warning';
  if (cap.competencies === 'required') {
    const anyMapped = questions.some((q) => Object.keys(q.competency_map || {}).length > 0);
    if (!anyMapped) return 'warning';
  }
  // Empty sections.
  for (const s of sections) {
    const inSection = questions.filter((q) => q.section_id === s.id);
    if (inSection.length === 0) return 'warning';
  }
  return 'healthy';
}

function computeRecommendations(questionnaire, assetTypeMeta, sections, questions, summary, t) {
  const recs = [];
  // Family 1 — difficulty balance
  const difficulties = questions.map((q) => q.difficulty).filter((d) => typeof d === 'number');
  if (difficulties.length >= 5) {
    const counts = {};
    difficulties.forEach((d) => { counts[d] = (counts[d] || 0) + 1; });
    const max = Math.max(...Object.values(counts));
    if (max / difficulties.length >= 0.7) {
      recs.push({ id: 'difficulty-balance', severity: 'info',
        family: t('studio.recs.difficultyFamily', { defaultValue: 'Difficulty balance' }),
        title: t('studio.recs.difficultyTitle', { defaultValue: 'Consider redistributing difficulty' }),
        message: t('studio.recs.difficultyMsg', { defaultValue: 'More than 70% of content items share one difficulty level. A mix improves discrimination.' }) });
    }
  }
  // Family 2 — estimated completion time
  const heuristic = estimateMinutes(questions);
  const stated = questionnaire.estimated_minutes;
  if (stated && heuristic && Math.abs(stated - heuristic) / Math.max(heuristic, 1) > 0.25) {
    recs.push({ id: 'estimated-time', severity: 'info',
      family: t('studio.recs.timeFamily', { defaultValue: 'Estimated completion time' }),
      title: t('studio.recs.timeTitle', { defaultValue: 'Time estimate may be off' }),
      message: t('studio.recs.timeMsg', { defaultValue: 'The configuration states {{stated}} min while the automatic calculation suggests about {{heuristic}} min. Re-check based on the type and number of items.', stated, heuristic }) });
  }
  // Family 3 — competency coverage
  if (summary.competencies > 0) {
    const counts = {};
    questions.forEach((q) => Object.keys(q.competency_map || {}).forEach((c) => { counts[c] = (counts[c] || 0) + 1; }));
    const undercovered = Object.entries(counts).filter(([, n]) => n < 2).length;
    if (undercovered / Math.max(summary.competencies, 1) > 0.5) {
      recs.push({ id: 'competency-coverage', severity: 'warning',
        family: t('studio.recs.coverageFamily', { defaultValue: 'Competency coverage' }),
        title: t('studio.recs.coverageTitle', { defaultValue: 'Competencies need more measures' }),
        message: t('studio.recs.coverageMsg', { defaultValue: 'Less than half of mapped competencies have ≥ 2 content items measuring them.' }) });
    }
  }
  // Family 4 — configuration completeness
  const cap = assetTypeMeta?.capabilities || questionnaire.capabilities || {};
  const missing = [];
  if (cap.passing_score === 'required' && questionnaire.passing_score == null) missing.push(t('studio.recs.fieldPassingScore', { defaultValue: 'Passing Score' }));
  if (cap.estimated_time === 'required' && questionnaire.estimated_minutes == null) missing.push(t('studio.recs.fieldEstimatedTime', { defaultValue: 'Estimated Time' }));
  if (missing.length) {
    recs.push({ id: 'config-complete', severity: 'warning',
      family: t('studio.recs.configFamily', { defaultValue: 'Configuration completeness' }),
      title: t('studio.recs.configTitle', { defaultValue: 'Required fields are empty' }),
      message: t('studio.recs.configMsg', { defaultValue: '{{fields}} required by the asset type.', fields: missing.join(', ') }) });
  }
  // Family 5 — validation improvements (placeholder until 2C wires the validator)
  if (questions.length > 0 && questions.some((q) => !q.prompt || q.prompt.trim().length < 5)) {
    recs.push({ id: 'validation-prompts', severity: 'warning',
      family: t('studio.recs.promptsFamily', { defaultValue: 'Validation improvements' }),
      title: t('studio.recs.promptsTitle', { defaultValue: 'Some prompts look incomplete' }),
      message: t('studio.recs.promptsMsg', { defaultValue: 'A few content items have very short prompts. Consider adding context.' }) });
  }
  return recs;
}
